import { QueryClientProvider } from "@tanstack/react-query"
import notification, { IconType } from "antd/es/notification"
import { ReactElement, lazy, useEffect } from "react"
import { BrowserRouter, Route, Routes } from "react-router"

import { queryClient } from "@/api/client"
import ProtectedRoute from "@/components/frame/ProtectedRoute"
import PreviewBanner from "@/components/PreviewBanner"
import ConfigProvider from "@/containers/ConfigProvider"
import { identify } from "@/util/analytics"
import viewManager from "@/util/viewManager"

const LoginPage = lazy(
  () => import(/* webpackChunkName: "LoginPage" */ "@/components/login/LoginPage"),
)
const SignupPage = lazy(
  () => import(/* webpackChunkName: "SignupPage" */ "@/components/login/SignupPage"),
)
const SelectOrgPage = lazy(
  () =>
    import(/* webpackChunkName: "SelectOrgPage" */ "@/components/login/SelectOrgPage"),
)
const VerifyMagicLinkPage = lazy(
  () =>
    import(
      /* webpackChunkName: "VerifyMagicLinkPage" */ "@/components/login/VerifyMagicLinkPage"
    ),
)
const LeadDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "LeadDetailsPage" */ "@/components/login/LeadDetailsPage"
    ),
)
const ImporterLauncherErrorPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ImporterLauncherErrorPage" */ "@/components/importer/launcher/ImporterLauncherErrorPage"
    ),
)
const ImporterLauncherPage = lazy(
  () =>
    import(
      /* webpackChunkName: "ImporterLauncherPage" */ "@/components/importer/launcher/ImporterLauncherPage"
    ),
)
const Frame = lazy(
  () => import(/* webpackChunkName: "Frame" */ "@/components/frame/Frame"),
)
const FileFeedsEmbedTransformsLauncher = lazy(
  () =>
    import(
      /* webpackChunkName: "FileFeedsEmbedTransformsLauncher" */ "@/components/fileFeeds/embedded/FileFeedsEmbedTransformsLauncher"
    ),
)

const openNotificationWithIcon = (type: IconType, message: string) => {
  notification[type]({ message })
}

// Supports routing between the main frame and various widget pages
export default function BaseRouter(): ReactElement | null {
  const org = viewManager.get("org")
  const user = viewManager.get("user")
  useEffect(
    () => {
      const flash = viewManager.get("flash")
      if (flash) {
        openNotificationWithIcon(flash.type, flash.text)
      }

      if (org && user) {
        identify(org, user)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // If you add a route here, you'll have to manually add a
  // handler in controllers/main.rb.
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PreviewBanner />
        <Routes>
          <Route path="/login/*" element={<LoginPage />} />
          <Route path="/signup/*" element={<SignupPage />} />
          <Route
            path="/introduce-yourself/*"
            element={<LeadDetailsPage user={user} org={org} />}
          />
          <Route path="/select-organization/*" element={<SelectOrgPage />} />
          <Route path="/verify-magic-link/*" element={<VerifyMagicLinkPage />} />
          <Route path="/embed-launcher/*" element={<ImporterLauncherPage />} />
          {/**
           * customizations won't exist for this page because there is no org
           * but components can rely on having the provider available.
           * Eventually this might/should have the same entry point as `/embed-launcher`
           * and that place should do the provider wrapping for both instead
           *  */}
          <Route
            path="/embed-launcher-error/*"
            element={
              <ConfigProvider>
                <ImporterLauncherErrorPage />
              </ConfigProvider>
            }
          />
          <Route
            path="/file-feeds-embed/launcher/*"
            element={<FileFeedsEmbedTransformsLauncher />}
          />
          <Route path="*" element={<ProtectedRoute />}>
            <Route path="*" element={<Frame />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
